const DEFAULT_INTERVAL = 4000; // ms each item is shown
const DEFAULT_FADE = 500; // ms fade duration

class RotatingText extends HTMLElement {
  constructor() {
    super();
    this._items = [];
    this._current = 0;
    this._intervalId = null;
    this._observer = null;
    this._resizeObserver = null;
    this._boundResize = null;
  }

  connectedCallback() {
    // small timeout to allow children to be parsed/upgraded
    requestAnimationFrame(() => this._init());
    this._boundResize = () => this._measureAndSetHeight();
    window.addEventListener('resize', this._boundResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._boundResize);
    this._stop();
    if (this._observer) this._observer.disconnect();
    if (this._resizeObserver) this._resizeObserver.disconnect();
  }

  _init() {
    // find element children (skip text nodes)
    this._items = Array.from(this.children).filter(n => n.nodeType === 1);
    if (!this._items.length) return;

    // apply base host styles if not already set
  // ensure the custom element behaves like a block container
  if (!this.style.display) this.style.display = 'block';
  if (!this.style.position) this.style.position = 'relative';
  this.style.overflow = 'hidden';
  this.style.boxSizing = this.style.boxSizing || 'border-box';

    // Respect prefers-reduced-motion: show all, no animation
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this._items.forEach(i => {
        i.style.position = 'static';
        i.style.opacity = '1';
        i.style.transition = 'none';
      });
      this.style.height = 'auto';
      return;
    }

    // Apply per-item styles and ensure wrap
    this._items.forEach(el => {
      el.style.position = 'absolute';
      el.style.top = '0';
      el.style.left = '0';
      el.style.width = '100%';
      el.style.margin = '0';
      el.style.boxSizing = 'border-box';
  el.style.whiteSpace = 'normal';
      el.style.wordBreak = 'break-word';
      el.style.overflowWrap = 'anywhere';
  // prevent inherited extreme line-height from stretching the item
  if (!el.style.lineHeight) el.style.lineHeight = '1.2';
      el.style.opacity = '0';
      el.style.transition = `opacity ${this._fadeDuration}ms ease`;
      el.setAttribute('aria-hidden', 'true');
    });

    // measure and set height to tallest item
    this._measureAndSetHeight();

    // show first item immediately
    this._show(0);

    const interval = this._interval;
    this._intervalId = setInterval(() => this._next(), interval);

    // observe children changes (if items change dynamically)
    this._observer = new MutationObserver((m) => {
      // simple re-init on childList changes
      if (m.some(rec => rec.type === 'childList')) this._reinit();
    });
    this._observer.observe(this, { childList: true });

    // observe resize of host to recompute height when width changes
    if ('ResizeObserver' in window) {
      this._resizeObserver = new ResizeObserver(() => this._measureAndSetHeight());
      this._resizeObserver.observe(this);
    }
  }

  _measureAndSetHeight() {
    // Deprecated: use _setHostHeightForIndex when showing a specific item.
    // Keep a safe fallback: if there are no items, do nothing.
    if (!this._items.length) return;
    // Set host height to current item's measured height
    this._setHostHeightForIndex(this._current, false);
  }

  _measureItemHeight(item) {
    if (!item) return 0;
    const rect = this.getBoundingClientRect();
    const temp = document.createElement('div');
    temp.style.position = 'absolute';
    temp.style.visibility = 'hidden';
    temp.style.pointerEvents = 'none';
    temp.style.width = rect.width + 'px';
    temp.style.boxSizing = 'border-box';
    document.body.appendChild(temp);

    const clone = item.cloneNode(true);
    clone.style.position = 'static';
    clone.style.opacity = '1';
    clone.style.visibility = 'visible';
    clone.style.width = '100%';
    clone.style.transition = 'none';
    clone.style.boxSizing = 'border-box';
    clone.style.whiteSpace = 'normal';
    temp.appendChild(clone);
    const h = clone.getBoundingClientRect().height;
    document.body.removeChild(temp);
    return Math.ceil(h) || 0;
  }

  _setHostHeightForIndex(index, animate = true) {
    const item = this._items[index];
    if (!item) return;
    const height = this._measureItemHeight(item);
    if (!height) return;
    // set transition for height (use fadeDuration as default)
    const dur = this._fadeDuration;
    if (animate) this.style.transition = `height ${dur}ms ease`;
    else this.style.transition = 'none';
    // Force reflow before setting height to ensure transition works
    // eslint-disable-next-line no-unused-expressions
    this.getBoundingClientRect();
    this.style.height = height + 'px';
  }

  _show(index) {
    // Measure the target item's height and animate host height, then cross-fade
    const target = index;
    this._setHostHeightForIndex(target, true);
    // wait a frame so height transition starts, then cross-fade
    requestAnimationFrame(() => {
      this._items.forEach((el, i) => {
        if (i === target) {
          el.style.opacity = '1';
          el.removeAttribute('aria-hidden');
        } else {
          el.style.opacity = '0';
          el.setAttribute('aria-hidden', 'true');
        }
      });
      this._current = target;
    });
  }

  _next() {
    const next = (this._current + 1) % this._items.length;
    this._show(next);
  }

  _stop() {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  }

  _reinit() {
    this._stop();
    // small debounce
    clearTimeout(this._reinitTimeout);
    this._reinitTimeout = setTimeout(() => {
      // reset state
      this._items = Array.from(this.children).filter(n => n.nodeType === 1);
      this._current = 0;
      this._init();
    }, 50);
  }

  get _interval() {
    const val = parseInt(this.getAttribute('data-interval'));
    return (val && val > 0) ? val : DEFAULT_INTERVAL;
  }

  get _fadeDuration() {
    const val = parseInt(this.getAttribute('data-fade'));
    return (val && val >= 0) ? val : DEFAULT_FADE;
  }
}

if (!customElements.get('rotating-text')) {
  customElements.define('rotating-text', RotatingText);
}

// Expose for debugging
window.RotatingText = RotatingText;
